import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './app.js';
import './styles.css';
import { ledger, waterfall } from './platform/browser.js';
import { installGlobalHooks } from './platform/global-hooks.js';
import { installTouchBehaviour } from './platform/touch-behaviour.js';
import { setupUpdates } from './update/register-sw.js';
import { setWaitingApply } from './update/waiting-apply.js';
import { initMonitoring, isMonitoringActive, reportLayoutSnapshot } from './platform/monitoring.js';
import { startLayoutProbe } from './platform/layout-probe.js';
import { initLanguage } from './platform/i18n.js';
import { initTheme } from './platform/theme.js';
import { startPhotoQueue } from './platform/photo-queue.js';
import { requestPersistentStorage } from './platform/storage-persist.js';

// Before anything else: an error during boot is exactly the one worth capturing,
// and a monitor initialised after the failure it should have caught is decoration.
initMonitoring();

// Before the first render, so nothing paints in the wrong language and then swaps —
// a flash of English at a Dutch user is exactly the kind of small wrongness that
// makes an interface feel unfinished.
initLanguage();

// Same reasoning, for appearance: resolve the theme before the first paint so the
// app never flashes light at someone whose device is dark.
initTheme();

waterfall.stamp('init');

installGlobalHooks((message) => {
  // Replaced by a real toast when the component library lands (Phase 1 step 1).
  // Until then it is loud on purpose: a silent failure is the thing being prevented.
  window.setTimeout(() => window.alert(message), 0);
});

// App-like touch behaviour: no long-press context menu outside editable fields (the CSS half —
// tap-highlight, callout, text selection — is in styles.css).
installTouchBehaviour();

const container = document.getElementById('root');
if (!container) {
  ledger.record(new Error('#root is missing from index.html'), 'boot', { notified: false });
  throw new Error('#root is missing');
}

createRoot(container).render(
  <StrictMode>
    <App />
  </StrictMode>,
);

/**
 * The offline photo queue, hydrated AFTER the first render (offline increment 2).
 *
 * After, not before: opening IndexedDB and replaying a stockroom's backlog must never
 * stand between a user and a painted screen. The queue is durable — nothing is lost by
 * starting it a beat late — whereas a boot that blocks on storage is the cold-start
 * regression this project has already measured once.
 *
 * A store that cannot be opened (Firefox private mode, Safari with storage disabled)
 * REJECTS rather than reporting an empty queue, and the rejection is ledgered. Reading
 * "nothing pending" off a broken database is precisely the silent failure G10 forbids.
 */
void startPhotoQueue().catch((error: unknown) => {
  ledger.record(error, 'boot:photo-queue', { notified: true });
});

// Ask to keep the offline queue (iOS clears script-writable storage after ~7 days). After the
// first render for the same reason the photo queue is: never between the user and a painted
// screen. Best-effort, and the outcome is recorded either way — see the module.
void requestPersistentStorage();

// THE iOS LAYOUT PROBE (0.1.520, off after 12 Nov 2026) — measures each new layout state on an
// iPhone and sends it to Sentry, so the field's iOS bug arrives as numbers without anyone in the
// atelier reporting it. A no-op on every other device. After the first render, like the two above:
// it measures the painted layout, so before the paint there is nothing to measure.
startLayoutProbe({
  send: (snapshot) => {
    if (isMonitoringActive()) {
      reportLayoutSnapshot(snapshot);
      return;
    }
    // No DSN in this build (a local or e2e build, or `?layoutprobe=1` on a dev machine): printed,
    // not dropped, so the probe is still observable where Sentry is not.
    console.info('[layout-probe]', JSON.stringify(snapshot));
  },
  // ⚠️ `notified: false` IS A DELIBERATE G10 DEVIATION: this is diagnostic telemetry the person on
  // the phone never asked for, and a toast about it would be noise they cannot act on. The ledger
  // entry still reaches Sentry through `ledger.onEntry`, which is the half that reaches us — and
  // the probe reports its first failure only, so a broken probe cannot flood either.
  fail: (error) => {
    ledger.record(error, 'layout-probe', { notified: false });
  },
});

setupUpdates({
  // Publish the apply as state; <UpdatePrompt> (mounted in the App) renders the redesign Confirm
  // from it (S2-c retired the blocking `window.confirm`). Never mid-interaction: ask, do not act.
  onWaiting: (apply) => setWaitingApply(apply),
});
